import { useEffect, useRef } from "react";
import { useAudioVideo, useMeetingManager } from "amazon-chime-sdk-component-library-react";
import { toast } from "sonner";
import { useShallow } from "zustand/react/shallow";
import { usePlatformStore } from "@platform/store";
import { useDevices } from "../providers/DevicesProvider";
import { suggestedNonContinuityMic } from "../utils/suggestedNonContinuityMic";
import { logIPhoneMicAlertEvent } from "./useIPhoneMicAlert";
import { IPhoneMicToast } from "../components/IPhoneMicToast";

/**
 * In-meeting consumer of `pendingIPhoneMicAlert`. When the alert is set AND
 * a Chime session is active, renders the `IPhoneMicToast` component via
 * `toast.custom(...)` from sonner. The toast is persistent (no auto-dismiss)
 * — the user must Switch or Dismiss.
 *
 * `duration: Infinity` is load-bearing — sonner's default auto-dismisses after
 * a few seconds, which would silently regress the persistent-visibility
 * requirement on a future refactor. Asserted in the test.
 */
export function useIPhoneMicToast(): void {
  const audioVideo = useAudioVideo();
  const meetingManager = useMeetingManager();
  const { audioInputDevices, selectAudioInputDevice } = useDevices();

  const { pending, setPending, setUserExplicitlyChose } = usePlatformStore(
    useShallow((s) => ({
      pending: s.pendingIPhoneMicAlert,
      setPending: s.setPendingIPhoneMicAlert,
      setUserExplicitlyChose: s.setUserExplicitlyChoseIPhoneMic,
    })),
  );

  const activeToastIdRef = useRef<string | number | null>(null);

  useEffect(() => {
    if (!audioVideo || !pending) {
      if (activeToastIdRef.current !== null) {
        toast.dismiss(activeToastIdRef.current);
        activeToastIdRef.current = null;
      }
      return;
    }

    const currentLabel = pending.currentDeviceLabel;

    const handleSwitch = async () => {
      // Re-resolve the target against current devices — the originally-stored
      // id may no longer be enumerated (Bluetooth disconnect, USB unplug,
      // AirPods power-off between trigger and click).
      const target = suggestedNonContinuityMic(audioInputDevices);
      if (!target) {
        toast.error("No alternative microphone available right now.");
        setPending(null);
        return;
      }

      try {
        await selectAudioInputDevice(target.deviceId);
        await meetingManager.startAudioInputDevice(target.deviceId);
        logIPhoneMicAlertEvent("iphone_mic_alert_switched", {
          surface: "meeting",
          from_device_label: currentLabel,
          to_device_label: target.label,
        });
      } catch {
        toast.error(`Failed to switch to ${target.label}.`);
      } finally {
        setPending(null);
      }
    };

    const handleUseAnyway = () => {
      setUserExplicitlyChose(true);
      logIPhoneMicAlertEvent("iphone_mic_kept", {
        current_device_label: currentLabel,
      });
      setPending(null);
    };

    const handleDismiss = () => {
      logIPhoneMicAlertEvent("iphone_mic_alert_dismissed", {
        surface: "meeting",
        current_device_label: currentLabel,
      });
      setPending(null);
    };

    const id = toast.custom(
      (toastId) => (
        <IPhoneMicToast
          currentDeviceLabel={currentLabel}
          suggestedTargetLabel={pending.suggestedTargetLabel}
          onSwitch={async () => {
            await handleSwitch();
            toast.dismiss(toastId);
          }}
          onUseAnyway={() => {
            handleUseAnyway();
            toast.dismiss(toastId);
          }}
          onDismiss={() => {
            handleDismiss();
            toast.dismiss(toastId);
          }}
        />
      ),
      { duration: Infinity },
    );

    activeToastIdRef.current = id;

    return () => {
      toast.dismiss(id);
      activeToastIdRef.current = null;
    };
  }, [
    audioVideo,
    pending,
    audioInputDevices,
    selectAudioInputDevice,
    meetingManager,
    setPending,
    setUserExplicitlyChose,
  ]);
}
